import styles from './sidebar.module.scss';
import classNames from 'classnames';
import { ReactNode, forwardRef } from 'react';
import SidebarCollapseButton from './SidebarCollapseButton';
import useCollapsableSidebarState from '@app/core/hooks/useLocalStoreSidebarCollapse';

export enum SidebarVariation {
  Empty = 'empty',
}

export enum SidebarModifier {
  Primary = 'primary',
}

interface SidebarProps {
  children: ReactNode;
  actions?: ReactNode;
  sidebarModifier?: SidebarModifier;
  sidebarVariation?: SidebarVariation;
  isErfModal?: boolean;
  className?: string;
  collapsible?: boolean;
  collapsibleStorageKey?: string;
  reverseCollapseButton?: boolean;
  containerClassName?: string;
  collapsedClassName?: string;
  containerTestId?: string;
  collapseState?: { isCollapsed: boolean; toggleSidebar: () => void };
}

const PARENT_CLASSNAME = 'sidebar';

const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(
  (
    {
      children,
      sidebarVariation,
      sidebarModifier,
      actions,
      className,
      isErfModal = false,
      collapsible = false,
      collapsibleStorageKey,
      reverseCollapseButton = false,
      containerClassName,
      collapsedClassName,
      containerTestId,
      collapseState,
      ...rest
    },
    ref
  ): JSX.Element => {
    const [internalIsCollapsed, internalToggleSidebar] = useCollapsableSidebarState(
      collapsibleStorageKey ?? 'collapsed-sidebar'
    );

    const isCollapsed = collapseState?.isCollapsed ?? internalIsCollapsed;
    const toggleSidebar = collapseState?.toggleSidebar ?? internalToggleSidebar;

    const sidebarContent = (
      <div
        ref={ref}
        className={classNames(className, {
          [styles[PARENT_CLASSNAME]]: !isErfModal,
          [styles[`${PARENT_CLASSNAME}--erf`]]: isErfModal,
          [styles[`${PARENT_CLASSNAME}--with-actions`]]: actions,
          [styles[`${PARENT_CLASSNAME}--${sidebarModifier}`]]: sidebarModifier,
        })}
        {...rest}>
        <div
          className={classNames(styles[`${PARENT_CLASSNAME}__content`], {
            [styles[`${PARENT_CLASSNAME}__content--${sidebarVariation}`]]: sidebarVariation,
          })}>
          {children}
        </div>
        {actions}
      </div>
    );

    if (!collapsible) {
      return sidebarContent;
    }

    return (
      <div
        className={classNames(containerClassName, {
          [collapsedClassName!]: isCollapsed && collapsedClassName,
        })}
        data-testid={containerTestId}>
        {reverseCollapseButton && (
          <SidebarCollapseButton
            toggleSidebar={toggleSidebar}
            isSidebarCollapsed={isCollapsed}
            reversed
          />
        )}
        {sidebarContent}
        {!reverseCollapseButton && (
          <SidebarCollapseButton toggleSidebar={toggleSidebar} isSidebarCollapsed={isCollapsed} />
        )}
      </div>
    );
  }
);

Sidebar.displayName = 'Sidebar';
export default Sidebar;
